import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#333333",
  },
  section: {
    margin: 10,
    padding: 10,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#2c3e50",
    borderBottomWidth: 1,
    borderBottomColor: "#ecf0f1",
    paddingBottom: 5,
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#555555",
    width: 160,
    marginRight: 20,
  },
  detailValue: {
    fontSize: 11,
    color: "#333333",
    flex: 1,
  },
  multilineValue: {
    fontSize: 11,
    color: "#333333",
    flex: 1,
    lineHeight: 1.3,
  },
  quotesOnlyNotice: {
    backgroundColor: "#fff3cd",
    padding: 15,
    margin: 10,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: "#ffc107",
  },
  quotesOnlyText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#856404",
    textAlign: "center",
  },
  pricingSection: {
    backgroundColor: "#e8f5e8",
    borderLeftWidth: 4,
    borderLeftColor: "#28a745",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    color: "#888888",
  },
  footerLogo: {
    height: 40,
    width: "auto",
    marginBottom: 8,
  },
  footerText: {
    textAlign: "center",
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#3498db",
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#3498db",
    marginBottom: 5,
  },
  shipperInfo: {
    fontSize: 12,
    color: "#333333",
    marginBottom: 5,
  },
  shipmentInfo: {
    textAlign: "right",
  },
  shipmentRef: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#333333",
    marginBottom: 5,
  },
  shipmentId: {
    fontSize: 11,
    color: "#666666",
  },
});

export interface ShipmentPDFShipment {
  shipment_company?: { name: string };
  shipment_creator?: { first_name: string; last_name: string };
  custom_name?: string | null;
  info: { id: number };
}

interface ShipmentPDFShipmentSource {
  supplier_company?: { name: string };
  supplier?: { user?: { first_name: string; last_name: string } } | null;
  info: { id: number; custom_name?: string | null };
}

export function buildShipmentPDFShipment(
  shipment: ShipmentPDFShipmentSource,
): ShipmentPDFShipment {
  return {
    shipment_company: { name: shipment.supplier_company?.name ?? "" },
    shipment_creator: shipment.supplier?.user
      ? {
          first_name: shipment.supplier.user.first_name,
          last_name: shipment.supplier.user.last_name,
        }
      : undefined,
    custom_name: shipment.info.custom_name,
    info: { id: shipment.info.id },
  };
}

interface ShipmentPDFProps {
  shipment: ShipmentPDFShipment;
  sections: AppRenderedPDFSectionConfig[];
  publicShipmentUrl?: string | null;
}

export function ShipmentPDF(props: ShipmentPDFProps) {
  const { shipment, sections, publicShipmentUrl } = props;

  const renderDetailRow = (
    label: string,
    value: string | number | undefined,
    multiline = false,
  ) => (
    <View style={styles.detailRow}>
      <Text style={styles.detailLabel}>{label}:</Text>
      <Text style={multiline ? styles.multilineValue : styles.detailValue}>
        {value ?? "-"}
      </Text>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section with Company and Shipment Info */}
        <View style={styles.headerSection}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {shipment?.shipment_company?.name}
            </Text>
            <Text style={styles.shipperInfo}>
              {shipment?.shipment_creator?.first_name}{" "}
              {shipment?.shipment_creator?.last_name}
            </Text>
            {shipment?.custom_name && (
              <Text style={styles.shipmentRef}>
                Shipment Ref: {shipment.custom_name}
              </Text>
            )}
            <Text style={styles.shipmentId}>
              Evotrux Shipment ID: {shipment?.info?.id}
            </Text>
          </View>
        </View>

        {/* Dynamic Sections */}
        {sections.map((section) => (
          <View key={section.id} style={styles.section}>
            <Text style={styles.sectionTitle}>{section.title}</Text>
            {section.items.map((item) => (
              <View key={item.label}>
                {renderDetailRow(
                  item.label,
                  item.value,
                  item.label.includes("Requirements") ||
                    item.label.includes("Details"),
                )}
              </View>
            ))}
          </View>
        ))}

        <View style={styles.footer}>
          <Image style={styles.footerLogo} src="/images/logo.jpg" />
          <Text style={styles.footerText}>
            Powered by Evotrux • {publicShipmentUrl ?? "https://evotrux.com"} •{" "}
            {new Date().toLocaleDateString()}
          </Text>
        </View>
      </Page>
    </Document>
  );
}
